/**
 * Structural composite-dependency recognition for the questionnaire builder.
 *
 * A question "feeds" a composite when one of its `metricRefs` is listed as a
 * component metric id on a committed composite
 * (`metricPlan.compositeMetrics[].components[].metricId`). This mirrors the
 * server validator (`packages/shared-types/src/questionnaire/validator.ts`
 * `collectCompositeMetricIds` / `validateCompositeBounds`). It works purely on the
 * structural signal — no `lp:` tags and no `lpMode` are required — so it is
 * correct for any study whose server-issued plan already carries composites,
 * and is a no-op (everything "free") for studies that have none.
 *
 * Everything here is a pure read over the server-issued draft. We never mutate
 * `metricPlan`; the server (T0/T1) owns pruning + renormalization when a scored
 * dimension is removed or retyped.
 */

import { resolveResearchQuestionFloor } from "@digital-twins/shared-types";
import type {
  WinnerCompositeDefinition,
  QuestionDefinition,
  QuestionnaireDraft,
} from "@digital-twins/shared-types";

type JsonObject = Record<string, unknown>;

function isRecord(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asArray(value: unknown): Array<unknown> {
  return Array.isArray(value) ? value : [];
}

function asDraftRecord(draft: unknown): JsonObject {
  return isRecord(draft) ? draft : {};
}

/** Tolerant read of a question's `metricRefs` (string entries only). */
function metricRefsOf(question: unknown): Array<string> {
  if (!isRecord(question)) {
    return [];
  }
  return asArray(question.metricRefs).filter(
    (ref): ref is string => typeof ref === "string",
  );
}

function compositeMetricIdsOf(composite: unknown): Set<string> {
  const ids = new Set<string>();
  if (!isRecord(composite)) {
    return ids;
  }
  asArray(composite.components).forEach((component) => {
    if (isRecord(component) && typeof component.metricId === "string") {
      ids.add(component.metricId);
    }
  });
  return ids;
}

function findResearchQuestion(
  draft: QuestionnaireDraft | null | undefined,
  questionId: string,
): QuestionDefinition | undefined {
  const draftRecord = asDraftRecord(draft);
  return asArray(draftRecord.researchQuestions).find(
    (question) => isRecord(question) && question.id === questionId,
  ) as QuestionDefinition | undefined;
}

/**
 * The committed composites a given question feeds (by structural metricRef ∩
 * component id). Empty for user-added / non-scored questions, unknown ids, and
 * non-LP drafts. Returns the live `WinnerCompositeDefinition` objects so callers
 * can name the affected score(s).
 */
export function getCompositesForQuestion(
  draft: QuestionnaireDraft | null | undefined,
  questionId: string,
): Array<WinnerCompositeDefinition> {
  const question = findResearchQuestion(draft, questionId);
  if (!question) {
    return [];
  }
  const metricRefs = new Set(metricRefsOf(question));
  if (metricRefs.size === 0) {
    return [];
  }

  const draftRecord = asDraftRecord(draft);
  const metricPlan = isRecord(draftRecord.metricPlan)
    ? draftRecord.metricPlan
    : {};
  const matches: Array<WinnerCompositeDefinition> = [];
  asArray(metricPlan.compositeMetrics).forEach((composite) => {
    if (!isRecord(composite)) {
      return;
    }
    const componentIds = compositeMetricIdsOf(composite);
    const feeds = [...metricRefs].some((ref) => componentIds.has(ref));
    if (feeds) {
      matches.push(composite as unknown as WinnerCompositeDefinition);
    }
  });
  return matches;
}

export type QuestionDeletionGuardKind =
  | "blocked_min_count"
  | "warn_scored"
  | "free";

export interface QuestionDeletionGuard {
  kind: QuestionDeletionGuardKind;
  /** Composites the question feeds (only meaningful for `warn_scored`). */
  composites: Array<WinnerCompositeDefinition>;
  /**
   * True when deleting this question removes the *last* live component of at
   * least one composite it feeds (the composite would no longer be scored). The
   * confirm copy escalates in that case. Always allowed — never a hard block.
   */
  isLastComponent: boolean;
}

/**
 * Classify what should happen when the user asks to delete a question.
 *
 * Order of precedence matches the hook's hard early-returns (which stay as the
 * safety net): the research min-count floor first, then the soft "scored
 * dimension" warn-and-confirm, else free/instant.
 *
 * Deleting a scored dimension is *valid-but-weaker* — the server prunes the
 * composite component and renormalizes the survivors — so it is never blocked.
 */
export function getQuestionDeletionGuard(
  draft: QuestionnaireDraft | null | undefined,
  questionId: string,
): QuestionDeletionGuard {
  const draftRecord = asDraftRecord(draft);
  const researchCount = asArray(draftRecord.researchQuestions).length;
  // The floor is 1: the only delete that is blocked is the one that would
  // leave the questionnaire empty.
  if (researchCount <= resolveResearchQuestionFloor(draft)) {
    return { kind: "blocked_min_count", composites: [], isLastComponent: false };
  }

  const composites = getCompositesForQuestion(draft, questionId);
  if (composites.length === 0) {
    return { kind: "free", composites: [], isLastComponent: false };
  }

  const question = findResearchQuestion(draft, questionId);
  const metricRefs = new Set(metricRefsOf(question));
  const isLastComponent = composites.some((composite) => {
    const componentIds = [...compositeMetricIdsOf(composite)];
    const remaining = componentIds.filter((id) => !metricRefs.has(id));
    return remaining.length === 0;
  });

  return { kind: "warn_scored", composites, isLastComponent };
}

/**
 * True when changing `questionId` to `nextType` would drop the `metric_*` ref
 * that currently feeds a composite — i.e. the question is scored today and the
 * new type silently guts the score. In the canonical model only `open_end`
 * swaps `metric_<sk>` for `qual_<sk>`; every other type keeps `metric_<sk>`, so
 * the composite ref survives a rating↔choice↔matrix retype.
 *
 * Mirror of `normalizeTypeForQuestion`'s metricRef assignment in
 * `useQuestionnaireWorkspace.ts` (open_end → `qual_`, else → `metric_`).
 */
export function questionTypeChangeDropsScoredRef(
  draft: QuestionnaireDraft | null | undefined,
  questionId: string,
  nextType: string,
): boolean {
  if (nextType !== "open_end") {
    return false;
  }
  return getCompositesForQuestion(draft, questionId).length > 0;
}
